import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, ClipboardListIcon, FolderOpenIcon, LineChartIcon, LockIcon, ScaleIcon, TableIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { useAssessment, useInstrumentConfig } from '../../contexts/AssessmentContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { ScenarioKeyTable } from '../../components/demo/ScenarioKeyTable';
import { LedgerTable } from '../../components/workspace/LedgerTable';
import { BudgetTable } from '../../components/workspace/BudgetTable';
import { BundleTable } from '../../components/workspace/BundleTable';
import { InstructionsPanel } from '../../components/workspace/InstructionsPanel';
import { DocumentViewer } from '../../components/workspace/DocumentViewer';
import { assessmentUrlFor } from '../../data/demoInstruments';
import { TabId } from '../../types/assessment';
type PreviewTab = TabId | 'key';
const TAB_ICONS: Record<TabId, typeof TableIcon> = {
  reconciliation: TableIcon,
  budget: LineChartIcon,
  documents: FolderOpenIcon,
  bundle: ScaleIcon,
  instructions: ClipboardListIcon
};
export function InstrumentPreview() {
  const {
    mode,
    slots,
    bundle
  } = useDemo();
  const {
    setInstrument
  } = useAssessment();
  const config = useInstrumentConfig();
  const [tab, setTab] = useState<PreviewTab>(config.defaultTab);
  const navigate = useNavigate();

  // The preview embeds the real workspace, so the engine follows the resolved instrument.
  useEffect(() => {
    setInstrument(bundle.instrument);
  }, [bundle.instrument, setInstrument]);
  useEffect(() => {
    if (tab !== 'key' && !config.tabs.some(t => t.id === tab)) setTab(config.defaultTab);
  }, [config, tab]);
  const TABS: {
    id: PreviewTab;
    label: string;
    icon: typeof TableIcon;
  }[] = [...config.tabs.map(t => ({
    id: t.id as PreviewTab,
    label: t.label,
    icon: TAB_ICONS[t.id]
  })), {
    id: 'key' as PreviewTab,
    label: 'Assessment key',
    icon: LockIcon
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.25,
    ease: [0.23, 1, 0.32, 1]
  }} className="flex min-h-full w-full flex-col bg-paper" data-id="element-694" data-mp="9kwbcyrx6wxp">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-695" data-mp="iwss219bksn">
        <LenticMark data-id="element-696" data-mp="3i0por4qyfsaj" />
        <BackLink to="/demo/pipeline" label="Back to the gates" data-id="element-697" data-mp="3i07idfakvxow" />
      </div>

      <main className="mx-auto w-full max-w-[1500px] flex-1 px-8 py-8" data-id="element-698" data-mp="3mqwe56tajgtu">
        <div className="flex flex-wrap items-center gap-3" data-id="element-699" data-mp="3mt0puaxflk4o">
          <h1 className="text-[20px] font-semibold tracking-tight text-ink" data-id="element-700" data-mp="3912mkgqeiigf">
            {bundle.label} · {bundle.matterLabel}
          </h1>
          <span className="rounded-sm border border-rule bg-white px-2.5 py-1 font-mono-num text-[11px] text-muted" data-id="element-701" data-mp="3cz0brjk694jt">
            {bundle.packageHash}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-sm border border-brand px-2.5 py-1 font-mono-num text-[11px] uppercase tracking-wider text-brand" data-id="element-702" data-mp="340574bws02im">
            <LockIcon className="h-3 w-3" data-id="element-703" data-mp="2fglcm15gm72p" />
            Locked
          </span>
        </div>

        <p className="mt-2 text-[13.5px] text-muted" data-id="element-704" data-mp="2udlvdf5oarp1">
          {mode === 'single' ? 'Tailored probe — comparability is moot by design.' : 'This is the candidate’s workspace — assistant included, sources withheld from it.'}
        </p>

        <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_300px]" data-id="element-705" data-mp="33bo46gvneomv">
          <div className="overflow-hidden rounded-sm border border-rule bg-white" data-id="element-706" data-mp="2ye4m15vtxrpn">
            <div className="flex items-center gap-1 border-b border-rule bg-paper px-3 pt-2.5" data-id="element-707" data-mp="3d28xbosjz0ye">
              {TABS.map(t => {
              const active = t.id === tab;
              return <button key={t.id} type="button" onClick={() => setTab(t.id)} aria-current={active ? 'page' : undefined} className={`flex items-center gap-2 rounded-t-sm border border-b-0 px-4 py-2 text-[13px] font-medium transition-colors duration-150 ease-settle ${active ? 'border-rule bg-white text-ink' : 'border-transparent text-muted hover:text-ink'}`} data-id="element-708" data-mp="3m51q5whdvkqc">
                    <t.icon className="h-3.5 w-3.5" data-id="element-709" data-mp="whx716ckjkjl" />
                    {t.label}
                  </button>;
            })}
            </div>
            <div className="h-[560px] overflow-hidden" data-id="element-710" data-mp="11i1nwxhe2hac">
              {tab === 'reconciliation' && <LedgerTable data-id="element-711" data-mp="16e09kdbc1l72" />}
              {tab === 'budget' && <BudgetTable data-id="element-712" data-mp="1ah58y70496rh" />}
              {tab === 'bundle' && <BundleTable data-id="element-713" data-mp="1aesfoxowr9z4" />}
              {tab === 'instructions' && <InstructionsPanel data-id="element-714" data-mp="2htf3174c1uxf" />}
              {tab === 'documents' && <DocumentViewer data-id="element-715" data-mp="2lw1zxmfv261h" />}
              {tab === 'key' && <div className="h-full overflow-y-auto px-5 py-5" data-id="element-716" data-mp="2lu8rc1lotbqu">
                  <p className="text-[13px] text-muted" data-id="element-717" data-mp="a7ncq8m4pzhi">
                    The buyer’s key. Every line the reviewer will need to read the record.
                  </p>
                  <div className="mt-5" data-id="element-718" data-mp="1g4xq10x5pq0">
                    <ScenarioKeyTable showCorrectAction data-id="element-719" data-mp="1f3lht862x6p" />
                  </div>
                </div>}
            </div>
          </div>

          <aside data-id="element-720" data-mp="3nnld8ngkuntj">
            <p className="eyebrow text-muted" data-id="element-721" data-mp="3no4lfcgfdxud">Scenario slots · gate results</p>
            <ul className="mt-4 space-y-2" data-id="element-722" data-mp="3ioi6myky89jg">
              {slots.map(slot => <li key={slot.id} className={`rounded-sm border border-rule bg-white p-3.5 ${slot.klass === 'CLEAN' ? 'border-l-2 border-l-brand' : ''}`} data-id="element-723" data-mp="3iqux604rvs84">
                  <div className="flex items-baseline justify-between gap-2" data-id="element-724" data-mp="1cuii2vwgrn1a">
                    <span className="font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-brand" data-id="element-725" data-mp="17vxhgvdav1cb">
                      {slot.klass}
                    </span>
                    <span className="font-mono-num text-[10.5px] text-muted" data-id="element-726" data-mp="1qyzdxecoxne9">{slot.row}</span>
                  </div>
                  <p className="mt-1.5 text-[12.5px] leading-snug text-ink" data-id="element-727" data-mp="1i0dyhs2imohp">{slot.title}</p>
                  <dl className="mt-2.5 space-y-1 font-mono-num text-[10.5px] text-muted" data-id="element-728" data-mp="1mzi3s7mbk22r">
                    <div className="flex gap-2" data-id="element-729" data-mp="1mx4aa37aexxb">
                      <dt className="w-[68px] shrink-0" data-id="element-730" data-mp="2aq4ds4fvvwfe">consistency</dt>
                      <dd className="text-ink" data-id="element-731" data-mp="2eqegn88dq6q1">{slot.consistency}</dd>
                    </div>
                    <div className="flex gap-2" data-id="element-732" data-mp="20r5c55tjd11w">
                      <dt className="w-[68px] shrink-0" data-id="element-733" data-mp="20qdezqkaiv9i">solvability</dt>
                      <dd className="leading-snug" data-id="element-734" data-mp="25p6v7qqyp0dd">{slot.solvability}</dd>
                    </div>
                  </dl>
                </li>)}
            </ul>
          </aside>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-735" data-mp="ubump2k9btkv">
          <button type="button" onClick={() => navigate('/demo/invite')} className="group inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark" data-id="element-736" data-mp="mrad1jhil59g">
            Invite candidates
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-737" data-mp="qtykbmqky44b" />
          </button>
          <button type="button" onClick={() => navigate('/demo/record')} className="text-[13px] text-muted underline decoration-rule underline-offset-4 transition-colors hover:text-ink" data-id="element-738" data-mp="318d1qtmg90d">
            Skip ahead — see what comes back
          </button>
          <a href={assessmentUrlFor(bundle.instrument)} target="_blank" rel="noreferrer" className="text-[13px] text-brand underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:decoration-brand" data-id="element-739" data-mp="80b6sf45ist1">
            Open the live assessment ↗
          </a>
        </div>
      </main>

      <DemoFooter data-id="element-740" data-mp="2uzehybq3ncu5" />
    </motion.div>;
}